import { NgModule } from '@angular/core';
import { RouteReuseStrategy, RouterModule, Routes } from '@angular/router';
import { ForceReloadRouteReuseStrategy } from './shared/strategies/force-reload-route-reuse.strategy';
import { UrlKeys } from './shared/enums/urls.enum';
import { adminGuard } from './shared/guards/admin.guard';
import { appGuard } from './shared/guards/app.guard';
import { authorizationGuard } from './shared/guards/authorization.guard';
import { certificateGuard } from './shared/guards/certificate.guard';
import { comoEnCasaGuard } from './shared/guards/comoencasa.guard';
import { drCignaGuard } from './shared/guards/drcigna.guard';
import { loggedGuard, notLoggedGuard } from './shared/guards/logged.guard';
import { reimbursementGuard } from './shared/guards/reimbursement.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: UrlKeys.LOGIN,
    pathMatch: 'full',
  },
  {
    path: UrlKeys.LOGIN,
    canActivate: [notLoggedGuard],
    loadChildren: () =>
      import('./pages/public/login/login-routing.module').then(m => m.LoginRoutingModule),
  },
  {
    path: UrlKeys.REGISTER,
    canActivate: [notLoggedGuard],
    loadChildren: () =>
      import('./pages/public/register/register-routing.module').then(m => m.RegisterRoutingModule),
  },
  {
    path: UrlKeys.RETRIEVE,
    loadChildren: () =>
      import('./pages/public/retrieve-password/retrieve-password-routing.module').then(
        m => m.RetrievePasswordRoutingModule
      ),
  },
  {
    path: UrlKeys.LOGOUT,
    loadComponent: () =>
      import('./pages/public/logout/components/logout/logout.component').then(
        m => m.LogoutComponent
      ),
  },
  {
    path: UrlKeys.CHAT_HOST,
    loadComponent: () =>
      import('./pages/public/chat-host/chat-host.component').then(m => m.ChatHostComponent),
  },
  /* -------------- */
  /* Rutas privadas */
  /* -------------- */
  {
    path: UrlKeys.HOME,
    canActivate: [loggedGuard],
    loadComponent: () =>
      import('./pages/private/home/components/home/home.component').then(m => m.HomeComponent),
  },
  {
    path: UrlKeys.NEXT_CITAS,
    canActivate: [loggedGuard],
    loadComponent: () =>
      import('./pages/private/home/components/next-citas/next-citas.component').then(
        m => m.NextCitasComponent
      ),
  },
  {
    path: UrlKeys.CHANGE_PASS,
    canActivate: [loggedGuard],
    loadComponent: () =>
      import(
        './pages/private/change-password/components/change-password/change-password.component'
      ).then(m => m.ChangePasswordComponent),
  },
  {
    path: UrlKeys.MI_CIGNA,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/mi-cigna/mi-cigna-routing.module').then(m => m.MiCignaRoutingModule),
  },
  {
    path: UrlKeys.REEMBOLSOS,
    canActivate: [loggedGuard, reimbursementGuard],
    loadChildren: () =>
      import('./pages/private/reembolsos/reembolsos-routing.module').then(
        m => m.ReembolsosRoutingModule
      ),
  },
  {
    path: UrlKeys.SOPORTE,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/soporte/soporte-routing.module').then(m => m.SoporteRoutingModule),
  },
  {
    path: UrlKeys.TELEMEDICINA,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/telemedicina/telemedicina-routing.module').then(
        m => m.TelemedicinaRoutingModule
      ),
  },
  {
    path: UrlKeys.MAS_SALUD,
    canActivate: [loggedGuard],
    loadComponent: () =>
      import('./pages/private/mas/components/cigna-mas-salud/cigna-mas-salud.component').then(
        m => m.CignaMasSaludComponent
      ),
  },
  {
    path: UrlKeys.SOL_REEMBOLSO,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/solicitud-reembolso/solicitud-reembolso-routing.module').then(
        m => m.SolicitudReembolsoRoutingModule
      ),
  },
  {
    path: UrlKeys.SOL_AUTORIZACION,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/solicitud-autorizacion/solicitud-autorizacion-routing.module').then(
        m => m.SolicitudAutorizacionRoutingModule
      ),
  },
  {
    path: UrlKeys.AUTORIZACIONES,
    canActivate: [loggedGuard, authorizationGuard],
    loadChildren: () =>
      import('./pages/private/autorizaciones/autorizaciones-routing.module').then(
        m => m.AutorizacionesRoutingModule
      ),
  },
  {
    path: UrlKeys.SOL_REEMB_HOSPITALIZACION,
    canActivate: [loggedGuard, comoEnCasaGuard],
    loadChildren: () =>
      import(
        './pages/private/solicitud-reembolso-hosp/solicitud-reembolso-hosp-routing.module'
      ).then(m => m.SolicitudReembolsoHospRoutingModule),
  },
  {
    path: UrlKeys.CITAS,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/citas/citas-routing.module').then(m => m.CitasRoutingModule),
  },
  {
    path: UrlKeys.SOL_CITA,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/solicitud-cita/solicitud-cita-routing.module').then(
        m => m.SolicitudCitaRoutingModule
      ),
  },
  {
    path: UrlKeys.SOL_CERTIFICADO,
    canActivate: [loggedGuard, certificateGuard],
    loadChildren: () =>
      import('./pages/private/solicitud-certificado/solicitud-certificado-routing.module').then(
        m => m.SolicitudCertificadoRoutingModule
      ),
  },
  {
    path: UrlKeys.CUADRO_MEDICO,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/cuadro-medico/cuadro-medico-routing.module').then(
        m => m.CuadroMedicoRoutingModule
      ),
  },
  {
    path: UrlKeys.TELEDERMA,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/telederma/telederma-routing.module').then(
        m => m.TeledermaRoutingModule
      ),
  },
  //iframe directorio medico
  {
    path: UrlKeys.CUADRO_MEDICO_IFRAME,
    canActivate: [loggedGuard],
    loadChildren: () =>
      import('./pages/private/cuadro-medico-iframe/cuadro-medico-iframe-routing.module').then(
        m => m.CuadroMedicoIframeRoutingModule
      ),
  },
  {
    path: UrlKeys.DR_CIGNA,
    canActivate: [loggedGuard, drCignaGuard],
    loadComponent: () =>
      import('./pages/private/dr-cigna/components/dr-cigna/dr-cigna.component').then(
        m => m.DrCignaComponent
      ),
  },
  {
    path: UrlKeys.SETTINGS,
    canActivate: [loggedGuard, appGuard],
    loadComponent: () =>
      import('./pages/private/settings/components/settings/settings.component').then(
        m => m.SettingsComponent
      ),
  },
  /* ----------- */
  /* Rutas admin */
  /* ----------- */
  {
    path: UrlKeys.MAINTENANCE,
    canActivate: [adminGuard],
    loadChildren: () =>
      import('./pages/private/maintenance/maintenance-routing.module').then(
        m => m.MaintenanceRoutingModule
      ),
  },
  /* -------------- */
  /* Quotation tool */
  /* -------------- */
  {
    path: UrlKeys.QUOTATION_TOOL,
    title:
      'Calcula online tu seguro de salud al mejor precio | Cigna healthcare – Calcular seguro',
    loadChildren: () =>
      import('./pages/quotation-tool/quotation-tool-routing.module').then(
        m => m.QuotationToolRoutingModule
      ),
  },

  /* --- */
  /* 404 */
  /* --- */
  {
    path: '**',
    loadComponent: () =>
      import('./pages/notfound/components/notfound/notfound.component').then(
        m => m.NotfoundComponent
      ),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      bindToComponentInputs: true,
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
  providers: [
    { provide: RouteReuseStrategy, useClass: ForceReloadRouteReuseStrategy },
  ],
})
export class AppRoutingModule {}
